/**
 * PWA Install Prompt Component
 * Shows smart install prompts for iOS and Android users
 * iOS: Visual guide for "Add to Home Screen"
 * Android: Uses native beforeinstallprompt event
 */
import { useState, useEffect } from 'react';
import { X, Share, Plus, Smartphone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if already dismissed
    const wasDismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
      }
    }

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
    const isAndroidDevice = /android/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // For Android/Chrome, listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!standalone && !wasDismissed) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS Safari, show after a delay if not standalone
    if (isIOSDevice && isSafari && !standalone && !wasDismissed) {
      // Show after user has been on the site for 10 seconds
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 10000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  const handleInstallAndroid = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  // Don't show if already installed, dismissed, or not mobile
  if (isStandalone || dismissed || !showPrompt) {
    return null;
  }

  // iOS Safari Install Guide
  if (isIOS) {
    return (
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up"
        data-testid="ios-install-prompt"
      >
        <div className="max-w-md mx-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <Smartphone className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Install Trader Titans</h3>
                <p className="text-slate-400 text-xs">Add to your home screen</p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>

          {/* Instructions */}
          <div className="p-4 space-y-4">
            <p className="text-slate-300 text-sm">
              Install for the best experience - works offline and launches instantly!
            </p>
            
            {/* Step 1 */}
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-blue-400 font-bold text-sm">1</span>
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">Tap the Share button</p>
                <div className="mt-2 flex items-center gap-2 text-slate-400 text-xs">
                  <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center">
                    <Share className="h-4 w-4 text-blue-400" />
                  </div>
                  <span>at the bottom of Safari</span>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-blue-400 font-bold text-sm">2</span>
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">Scroll down & tap</p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex items-center gap-2 bg-slate-800 px-3 py-2 rounded-lg">
                    <Plus className="h-4 w-4 text-slate-300" />
                    <span className="text-slate-300 text-sm">Add to Home Screen</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-400 font-bold text-sm">3</span>
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">Tap "Add" to install</p>
                <p className="text-slate-400 text-xs mt-1">
                  The app will appear on your home screen
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 bg-slate-800/50 flex items-center justify-center">
            <ChevronDown className="h-5 w-5 text-slate-500 animate-bounce" />
            <span className="text-slate-500 text-xs ml-2">Tap Share below to start</span>
          </div>
        </div>

        <style jsx>{`
          @keyframes slide-up {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </div>
    );
  }

  // Android/Chrome Install Prompt
  if (isAndroid && deferredPrompt) {
    return (
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up"
        data-testid="android-install-prompt"
      >
        <div className="max-w-md mx-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-6 w-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold">Install Trader Titans</h3>
              <p className="text-slate-400 text-sm">Add to home screen for quick access</p>
            </div>
            <button 
              onClick={handleDismiss}
              className="p-2 hover:bg-slate-800 rounded-lg"
              aria-label="Dismiss"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>
          <div className="mt-4 flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleDismiss}
              className="flex-1 border-slate-700"
            >
              Not now
            </Button>
            <Button 
              onClick={handleInstallAndroid}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500"
            >
              Install
            </Button>
          </div>
        </div>

        <style jsx>{`
          @keyframes slide-up {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
